import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent } from '../ui/dialog';
import { RefreshCw, Video, Play, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DartfishTab = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [videos, setVideos] = useState([]);
  const [totalVideos, setTotalVideos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);
  const [channelKey, setChannelKey] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dartfish/collections`, { headers });
      setCollections(res.data.CollectionInfoList || []);
    } catch (err) {
      console.error('Failed to fetch collections:', err);
    }
  };

  const fetchVideos = async (collId, pg = 0) => {
    if (!collId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/dartfish/collections/${collId}/videos?page=${pg}&page_size=20`, { headers });
      setVideos(res.data.VideoInfoList || []);
      setTotalVideos(res.data.TotalVideosCount || 0);
      if (res.data.channelKey) setChannelKey(res.data.channelKey);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCollections(); }, []);

  useEffect(() => {
    if (selectedCollection) {
      setPage(0);
      fetchVideos(selectedCollection, 0);
    }
  }, [selectedCollection]);

  const formatDuration = (ticks) => {
    const seconds = Math.floor(ticks / 10000000);
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4" data-testid="dartfish-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-bold uppercase tracking-wide">Dartfish Videos</h2>
        <div className="flex items-center gap-3">
          <Select value={selectedCollection} onValueChange={setSelectedCollection}>
            <SelectTrigger className="w-[260px]" data-testid="dartfish-collection-select">
              <SelectValue placeholder="Select collection" />
            </SelectTrigger>
            <SelectContent>
              {collections.map(c => (
                <SelectItem key={c.Id} value={String(c.Id)}>
                  {c.Name} ({c.MatchCount} videos)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => fetchVideos(selectedCollection, page)} data-testid="dartfish-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {selectedCollection && (
        <p className="text-sm text-muted-foreground">{videos.length} videos in collection</p>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {selectedCollection ? 'No videos in this collection' : 'Select a collection to view videos'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {videos.map(v => (
            <Card key={v.Id} className="overflow-hidden group hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveVideo(v)}>
              <div className="relative aspect-video bg-muted">
                {v.Thumbnail ? (
                  <img src={v.Thumbnail} alt={v.Name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-10 h-10 text-muted-foreground/40" />
                  </div>
                )}
                <div className="absolute bottom-2 right-2">
                  <Badge variant="secondary" className="bg-black/70 text-white text-[10px]">
                    {formatDuration(v.Duration)}
                  </Badge>
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                  <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              <CardContent className="p-3">
                <p className="font-medium text-sm truncate">{v.Name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(v.CreationDate).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalVideos > 20 && (
        <div className="flex justify-center gap-2 pt-4">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => { setPage(p => p - 1); fetchVideos(selectedCollection, page - 1); }}>
            Previous
          </Button>
          <span className="text-sm py-2 px-3">Page {page + 1} of {Math.ceil(totalVideos / 20)}</span>
          <Button variant="outline" size="sm" disabled={(page + 1) * 20 >= totalVideos} onClick={() => { setPage(p => p + 1); fetchVideos(selectedCollection, page + 1); }}>
            Next
          </Button>
        </div>
      )}

      {/* Video Player - opens in new tab since Dartfish blocks iframe embedding */}
      <Dialog open={!!activeVideo} onOpenChange={() => setActiveVideo(null)}>
        <DialogContent className="max-w-lg w-[90vw] p-0 overflow-hidden">
          {activeVideo && (
            <div className="p-6 text-center space-y-4">
              <div className="relative aspect-video bg-muted rounded-lg overflow-hidden mb-2">
                {activeVideo.Thumbnail ? (
                  <img src={activeVideo.Thumbnail} alt={activeVideo.Name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-16 h-16 text-muted-foreground/30" />
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-lg">{activeVideo.Name}</h3>
              <p className="text-sm text-muted-foreground">Duration: {formatDuration(activeVideo.Duration)}</p>
              <a
                href={`${activeVideo.Link}&ck=${channelKey}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button size="lg" className="gap-2">
                  <Play className="w-5 h-5" /> Watch Video
                </Button>
              </a>
              <p className="text-xs text-muted-foreground">Opens in Dartfish player — no login required</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DartfishTab;
